import React from "react";
import DateMaskedInput from "../DateMaskedInput";

/**
 * Field — Constellation-style form input used inside Enroll.
 * Label above, placeholder inside, left icon for visual hierarchy.
 * Auto-switches to a masked DateMaskedInput when `type === "date"`.
 */
export default function Field({
  icon: Icon,
  label,
  required,
  type = "text",
  value,
  onChange,
  onBlur,
  placeholder,
  testid,
  help,
  error,
  maxLength,
}) {
  const baseClass =
    "w-full h-12 pr-4 rounded-xl bg-white outline-none text-[14px] font-semibold placeholder:text-slate-400 placeholder:font-normal transition border";
  const stateClass = error
    ? "border-rose-400 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/15"
    : "border-slate-200 focus:border-velcor-green focus:ring-2 focus:ring-velcor-green/15";
  const inputClass = `${baseClass} ${stateClass} ${Icon ? "pl-11" : "pl-4"}`;

  return (
    <div>
      <label className="block text-[12px] font-bold text-slate-700 mb-1.5">
        {label}
        {required && <span className="text-rose-500 ml-0.5">*</span>}
      </label>
      <div className="relative">
        {Icon && (
          <Icon className={`absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none ${error ? "text-rose-500" : "text-slate-400"}`} />
        )}
        {type === "date" ? (
          <DateMaskedInput
            required={required}
            value={value}
            onChange={onChange}
            data-testid={testid}
            placeholder={placeholder || "MM / DD / YYYY"}
            className={`${inputClass} tracking-wide`}
          />
        ) : (
          <input
            type={type}
            required={required}
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur ? (e) => onBlur(e.target.value) : undefined}
            maxLength={maxLength}
            data-testid={testid}
            className={inputClass}
            aria-invalid={!!error}
            aria-describedby={error ? `${testid}-error` : undefined}
          />
        )}
      </div>
      {error && (
        <div
          id={`${testid}-error`}
          className="mt-1.5 text-[11.5px] text-rose-600 font-bold leading-snug flex items-center gap-1.5"
          data-testid={`${testid}-error`}
          role="alert"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="shrink-0"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          {error}
        </div>
      )}
      {!error && help && <div className="mt-1.5 text-[11.5px] text-slate-500 leading-snug">{help}</div>}
    </div>
  );
}
